import React, { useContext, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  useTheme,
  MenuItem,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

import updateUser from "../../../api/users/edit";
import { useAuth } from "../../../contexts/AuthContext";
import { useSnackbar } from "../../../contexts/InfoContext";
import { SpinnerContext } from "../../../contexts/SpinnerContext";
import CustomTextField from "../../Credit/component/CustomTextField";
import capitalizeString from "../../../utils/capitalizeString";
import useCollectionData from "../../../hooks/useCollectionData";

// Validation schema using Yup
const validationSchema = Yup.object().shape({
  fullName: Yup.string().required("Full Name is required"),
  phone: Yup.string()
    .matches(/^\d+$/, "Phone must be a valid number")
    .required("Phone is required"),
  branches: Yup.array()
    .min(1, "At least one branch must be selected")
    .required("Branch selection is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  bankAccount: Yup.number()
    .typeError("Bank Account must be a number")
    .positive("Bank Account must be a positive number")
    .required("Bank Account is required"),
  fullAddress: Yup.string().required("Full Address is required"),
  securityName: Yup.string().required("Security Name is required"),
  securityAddress: Yup.string().required("Security Address is required"),
  securityPhone: Yup.string()
    .matches(/^\d+$/, "Security Phone must be a valid number")
    .required("Security Phone is required"),
  salary: Yup.number()
    .positive("Salary must be a positive number")
    .typeError("Salary must be a number")
    .required("Salary is required"),
});

const SubAdminEditForm = ({ admin, isEditDialogOpen, closeEditDialog }) => {
  const { user, forgotPassword } = useAuth();
  const theme = useTheme();
  const { openSnackbar } = useSnackbar();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const { data: branches } = useCollectionData("branches");

  const branchOptions =
    branches?.map((item) => ({
      name: item.name,
      id: item.id,
    })) || [];

  // Update formik values when branch data is loaded
  useEffect(() => {
    if (branches && branches.length > 0) {
      let branchObjects = [];

      // Handle if admin.branches is a JSON string
      if (typeof admin.branches === "string") {
        try {
          branchObjects = JSON.parse(admin.branches);
        } catch (error) {
          console.error("Error parsing branches JSON:", error);
          branchObjects = [];
        }
      }
      // Handle if admin.branches is already an array
      else if (Array.isArray(admin.branches)) {
        branchObjects = admin.branches;
      } else if (admin.branchId) {
        const currentBranchIds = Array.isArray(admin.branchId)
          ? admin.branchId
          : [admin.branchId];
        const currentBranchNames = Array.isArray(admin.branchName)
          ? admin.branchName
          : [admin.branchName];

        branchObjects = currentBranchIds.map((id, index) => ({
          branchId: id,
          branchName: currentBranchNames[index] || "",
        }));
      }

      formik.setFieldValue("branches", branchObjects || []);
      formik.setFieldValue("numberOfBranches", branchObjects?.length || 0);
    }
  }, [branches, admin.branchId, admin.branchName, admin.branches]);

  const handleBranchChange = (event) => {
    const { value } = event.target;
    const selectedBranchIds =
      typeof value === "string" ? value.split(",") : value;

    const selectedBranches = branchOptions.filter((branch) =>
      selectedBranchIds.includes(branch.id)
    );

    // Create branch objects with both id and name
    const branchObjects = selectedBranches.map((branch) => ({
      branchId: branch.id,
      branchName: branch.name,
    }));

    formik.setFieldValue("branches", branchObjects || []);
    formik.setFieldValue("numberOfBranches", branchObjects?.length || 0);
  };
  // Handle form submission
  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const name = capitalizeString(values.fullName);
      values.fullName = name;
      values.nameChange = values.fullName !== admin.fullName;
      values.phoneChange = values.phone !== admin.phone;
      values.addressChange = values.fullAddress !== admin.fullAddress;
      values.branchIdChange =
        JSON.stringify(values.branches) !==
        JSON.stringify(admin.branches || []);
      if (values.branchIdChange) {
        values.oldBranchId = admin.branchId;
      }
      values.emailChange = values.email !== admin.email;
      values.salaryChange = values.salary !== admin.salary;
      if (values.salaryChange) {
        values.difference = values.salary - admin.salary;
      }

      if (!values.branches || values.branches.length === 0) {
        handleCloseForm();
        throw {
          response: {
            data: {
              message:
                "Branch information is not found. Please check your connection, refresh your browser, and try again.",
              type: "error",
            },
          },
        };
      }
      // Update the admin with the new data
      const res = await updateUser(user, admin.id, values, "subAdmin");

      if (values.email !== admin.email) {
        forgotPassword(values.email);
      }
      openSnackbar(res.data.message, "success");
      handleCloseForm();
    } catch (error) {
      if (error.response && error.response.data) {
        openSnackbar(
          error.response.data.message,
          error.response.data.type ? error.response.data.type : "error"
        );
      } else {
        openSnackbar(
          "An unexpected error occurred.Please kindly check your connection.",
          "error"
        );
      }
    }
    setIsSubmitting(false);
  };

  // Initialize form values with admin data when the component is mounted
  const formik = useFormik({
    initialValues: {
      fullName: admin.fullName,
      phone: admin.phone,
      branches: (() => {
        // Handle if branches is a JSON string
        if (typeof admin.branches === "string") {
          try {
            return JSON.parse(admin.branches);
          } catch (error) {
            console.error("Error parsing branches JSON:", error);
            return [];
          }
        }
        // Handle if branches is already an array
        if (Array.isArray(admin.branches)) {
          return admin.branches;
        }

        if (admin.branchId) {
          return (
            Array.isArray(admin.branchId) ? admin.branchId : [admin.branchId]
          ).map((id, index) => ({
            branchId: id,
            branchName: Array.isArray(admin.branchName)
              ? admin.branchName[index]
              : admin.branchName,
          }));
        }
        return [];
      })(),
      numberOfBranches: (() => {
        if (typeof admin.branches === "string") {
          try {
            const parsed = JSON.parse(admin.branches);
            return Array.isArray(parsed) ? parsed.length : 0;
          } catch (error) {
            return 0;
          }
        }
        if (Array.isArray(admin.branches)) {
          return admin.branches.length;
        }
        return Array.isArray(admin.branchId)
          ? admin.branchId.length
          : admin.branchId
          ? 1
          : 0;
      })(),
      email: admin.email,
      bankAccount: admin.bankAccount,
      fullAddress: admin.fullAddress,
      securityName: admin.securityName,
      securityAddress: admin.securityAddress,
      securityPhone: admin.securityPhone,
      salary: admin.salary,
      birthday: admin.birthday,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Close the form and reset it
  const handleCloseForm = () => {
    closeEditDialog();
    formik.resetForm();
  };

  return (
    <div>
      <Dialog
        open={isEditDialogOpen}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
          Edit SubAdmin
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.palette.background.alt }}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <CustomTextField
                label="Full Name"
                name="fullName"
                value={formik.values.fullName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Phone"
                name="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Bank Account"
                name="bankAccount"
                value={formik.values.bankAccount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Full Address"
                name="fullAddress"
                value={formik.values.fullAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Security Name"
                name="securityName"
                value={formik.values.securityName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Security Address"
                name="securityAddress"
                value={formik.values.securityAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Security Phone"
                name="securityPhone"
                value={formik.values.securityPhone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Salary"
                name="salary"
                value={formik.values.salary}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />
              <CustomTextField
                label="Date Of Birth"
                type="date"
                name="birthday"
                value={formik.values.birthday}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />
              <Grid item xs={12}>
                <TextField
                  select
                  name="branches"
                  variant="outlined"
                  fullWidth
                  multiple
                  value={
                    formik.values.branches?.map((branch) => branch.branchId) ||
                    []
                  }
                  onChange={handleBranchChange}
                  onBlur={formik.handleBlur}
                  label="Select Branches"
                  InputLabelProps={{
                    shrink: true,
                    style: {
                      marginTop: "8px",
                      color: theme.palette.secondary[100],
                    },
                  }}
                  SelectProps={{
                    multiple: true,
                    MenuProps: {
                      PaperProps: {
                        style: {
                          backgroundColor: theme.palette.background.alt,
                          color: "inherit",
                          maxHeight: 300,
                        },
                      },
                    },
                    renderValue: (selected) => (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          alignItems: "center",
                        }}
                      >
                        {selected.length === 0 ? (
                          <Typography variant="body2" color="textSecondary">
                            No branches selected
                          </Typography>
                        ) : (
                          selected.map((id, index) => {
                            const branch = branchOptions.find(
                              (b) => b.id === id
                            );
                            return (
                              <React.Fragment key={id}>
                                <Chip
                                  label={branch?.name || `Branchesss (${id})`}
                                  sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    fontWeight: 500,
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.primary.dark,
                                    },
                                  }}
                                  size="small"
                                />
                                {index < selected.length - 1 && (
                                  <Box
                                    sx={{
                                      width: "1px",
                                      height: "20px",
                                      backgroundColor: theme.palette.divider,
                                      mx: 0.5,
                                    }}
                                  />
                                )}
                              </React.Fragment>
                            );
                          })
                        )}
                      </Box>
                    ),
                  }}
                  error={
                    formik.touched.branches && Boolean(formik.errors.branches)
                  }
                  helperText={formik.touched.branches && formik.errors.branches}
                >
                  {branchOptions.length === 0 ? (
                    <MenuItem
                      disabled
                      sx={{
                        backgroundColor: theme.palette.background.alt,
                        color: theme.palette.secondary[100],
                        py: 1.5,
                        "&:not(:last-child)": {
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Typography variant="body2">
                          No branches found
                        </Typography>
                      </Box>
                    </MenuItem>
                  ) : (
                    branchOptions.map((option, index) => {
                      const isSelected =
                        formik.values.branches?.some(
                          (branch) => branch.branchId === option.id
                        ) || false;

                      return (
                        <MenuItem
                          key={option.id}
                          value={option.id}
                          sx={{
                            backgroundColor: isSelected
                              ? theme.palette.primary.main
                              : theme.palette.background.alt,
                            color: isSelected
                              ? theme.palette.primary.contrastText
                              : theme.palette.secondary[100],
                            py: 1.5,
                            "&:not(:last-child)": {
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                            "&:hover": {
                              backgroundColor: isSelected
                                ? theme.palette.primary.dark
                                : theme.palette.primary.light,
                              color: isSelected
                                ? theme.palette.primary.contrastText
                                : theme.palette.primary.contrastText,
                            },
                            "&.Mui-selected": {
                              backgroundColor: theme.palette.primary.main,
                              color: theme.palette.primary.contrastText,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2" fontWeight={500}>
                              {option.name}
                            </Typography>
                            {isSelected && (
                              <Box
                                sx={{
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  backgroundColor:
                                    theme.palette.primary.contrastText,
                                }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })
                  )}
                </TextField>
              </Grid>
              <Grid
                item
                xs={12}
                container
                alignItems="end"
                justifyContent="end"
              >
                <DialogActions
                  sx={{ backgroundColor: theme.palette.background.alt }}
                >
                  <Button
                    variant="contained"
                    onClick={handleCloseForm}
                    sx={{
                      color: theme.palette.secondary[100],
                      "&:hover": {
                        backgroundColor: theme.palette.background.alt,
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      color: theme.palette.secondary[100],
                      "&:hover": {
                        backgroundColor: theme.palette.background.alt,
                      },
                    }}
                  >
                    Submit
                  </Button>
                </DialogActions>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubAdminEditForm;
